import * as yup from 'yup';

import { FormProvider, useForm } from 'react-hook-form';
import React, { useCallback, useState } from 'react';

import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../shared/components/Button';
import { TextInput } from '../../shared/components/TextInput';
import { yupResolver } from '@hookform/resolvers/yup';
import { http } from '../../shared/services/http';
import { useNavigator } from '../../routing/navigator';
import { useParams } from 'react-router-dom';
import { showPublicToast } from '../../shared/utils/show-public-toast';
import { css } from '@emotion/css';

interface IResetPasswordForm {
  password: string;
  passwordConfirm: string;
}

const schema = yup.object().shape({
  password: yup
    .string()
    .min(10, 'Password must be at least 10 characters.')
    .required('Password is required'),
  passwordConfirm: yup
    .string()
    .required('Please retype your password.')
    .oneOf([yup.ref('password')], 'Your passwords do not match.'),
});

export const ResetPasswordForm: React.FC = () => {
  const methods = useForm<IResetPasswordForm>({
    defaultValues: {
      password: '',
      passwordConfirm: '',
    },
    shouldUseNativeValidation: false,
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });
  const navigator = useNavigator();
  const { passwordResetToken } = useParams<{ passwordResetToken: string }>();
  const [saving, setSaving] = useState(false);

  const onSubmit = useCallback(
    methods.handleSubmit(async (data) => {
      try {
        setSaving(true);
        await http.resetPassword({
          password: data.password,
          passwordResetToken: passwordResetToken || '',
        });
        showPublicToast('Password updated. You can sign in now.');
        navigator.toEmailSignIn();
      } catch (e) {
        showPublicToast('Link expired. Request a new one.');
        console.error(e);
      } finally {
        setSaving(false);
      }
    }),
    [],
  );

  return (
    <form onSubmit={onSubmit} className={formStyle}>
      <FormProvider {...methods}>
        <div>
          <TextInput name="password" placeHolder="Password" type="password" />
          <TextInput
            name="passwordConfirm"
            placeHolder="Confirm password"
            type="password"
          />
        </div>
        <Button
          variant={ButtonVariantEnum.FILLED}
          size={ButtonSizeEnum.LARGE}
          text="Set password"
          type="submit"
          fullWidth
          loading={saving}
        />
      </FormProvider>
    </form>
  );
};

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
